import type { AxiosError } from 'axios';
import axios from 'axios';
import b64ToBlob from 'b64-to-blob';
import fileSaver from 'file-saver';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { Org, Role } from '../../../enum';
import { APIStatus } from '../../../enum';
import { formatDateTimeByTimezone, getAbbreviatedTimezone, validateAsAdmin } from '../../../helpers/utils.helper';
import type { NameValue } from '../../../models/dashboard';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';
import type { AdminAdvocate } from '../admin-advocates/adminAdvocateSlice';
import { months } from './report.helper';

const { logError } = RaygunErrorHandlerService();

type TimesheetParams = {
  selected_org_code: Org | 'allOrgs';
  advocate_id: string | null;
  month: number;
  year: number;
};

type Timesheet = {
  chat_id: string;
  first_name: string;
  last_name: string;
  email: string;
  org_name?: string;
  org_timezone_name?: string;
  advocate_post_chat_survey_complete: boolean;
  thread_id: string;
  time_expired: boolean;
  trigger_timestamp: string;
  start_timestamp: string;
  end_timestamp: string;
};

type TimesheetsSliceType = {
  chatLogsCSV: string;
  timesheetPaymentCSV: string;
  timesheetStudentCSV: string;
  chatLogsCSVApiStatus: APIStatus;
  timesheetPaymentApiStatus: APIStatus;
  timesheetInvoiceApiStatus: APIStatus;
  timesheetStudentCSVApiStatus: APIStatus;
  selectedAdvocate: NameValue | null;
  timesheetParams: TimesheetParams;
  timesheetAdvocates: AdminAdvocate[];
};

const initialState: TimesheetsSliceType = {
  chatLogsCSV: '',
  timesheetPaymentCSV: '',
  timesheetStudentCSV: '',
  chatLogsCSVApiStatus: APIStatus.IDLE,
  timesheetPaymentApiStatus: APIStatus.IDLE,
  timesheetInvoiceApiStatus: APIStatus.IDLE,
  timesheetStudentCSVApiStatus: APIStatus.IDLE,
  selectedAdvocate: null,
  timesheetParams: {
    selected_org_code: 'allOrgs',
    advocate_id: null,
    month: new Date().getMonth() || 12,
    year: new Date().getMonth() ? new Date().getFullYear() : new Date().getFullYear() - 1,
  },
  timesheetAdvocates: [],
};

const createChatLogsCSV = (data: Timesheet[], role: Role | null): string => {
  const isAdmin = validateAsAdmin(role);
  const headers = `Chat id,First name,Last name,Email${
    isAdmin ? ',Organization,Timezone' : ''
  },Completed PCS,Thread id,Time for PCS expired,Triggered timestamp, Started timestamp, Ended timestamp\n`;
  const body = data
    .map((d) => {
      const format = 'YYYY-MM-DD h:mm:ss A (z)';
      d.trigger_timestamp = formatDateTimeByTimezone(d.trigger_timestamp, d.org_timezone_name, format);
      d.start_timestamp = formatDateTimeByTimezone(d.start_timestamp, d.org_timezone_name, format);
      d.end_timestamp = formatDateTimeByTimezone(d.end_timestamp, d.org_timezone_name, format);
      d.org_timezone_name = getAbbreviatedTimezone(d.org_timezone_name);

      if (!isAdmin) {
        delete d.org_name;
        delete d.org_timezone_name;
      }

      return Object.values(d).join();
    })
    .join('\n');
  return headers + body;
};

export const getChatLogsCSV = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'timesheets/getChatLogsCSV',
  async (_, thunkAPI) => {
    try {
      const timesheetParams = thunkAPI.getState().timesheetsSlice.timesheetParams;
      const body = {
        ...timesheetParams,
        selected_org_code: timesheetParams.selected_org_code === 'allOrgs' ? null : timesheetParams.selected_org_code,
      };
      const response = (await axios.post('v0_get_timesheet', body)) as Timesheet[];
      if (!response || response.length === 0) {
        return '';
      }
      const role = thunkAPI.getState().authSlice.currentUser.role;
      return createChatLogsCSV(response, role);
    } catch (e) {
      logError(e, ['timesheetsSlice', 'getChatLogsCSV']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getPaymentAmountsCSV = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'timesheets/getPaymentAmountsCSV',
  async (_, thunkAPI) => {
    try {
      const timesheetParams = thunkAPI.getState().timesheetsSlice.timesheetParams;
      const body = {
        ...timesheetParams,
        selected_org_code: timesheetParams.selected_org_code === 'allOrgs' ? null : timesheetParams.selected_org_code,
      };
      const response = (await axios.post('v0_get_payment_amounts', body)) as string; // csv
      return response ?? '';
    } catch (e) {
      logError(e, ['timesheetsSlice', 'getPaymentAmountsCSV']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getStudentHoursCSV = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'timesheets/getStudentHoursCSV',
  async (_, thunkAPI) => {
    try {
      const timesheetParams = thunkAPI.getState().timesheetsSlice.timesheetParams;
      const body = {
        ...timesheetParams,
        selected_org_code: timesheetParams.selected_org_code === 'allOrgs' ? null : timesheetParams.selected_org_code,
      };
      const response = (await axios.post('v0_get_student_online_hours', body)) as string; // csv
      return response ?? '';
    } catch (e) {
      logError(e, ['timesheetsSlice', 'getStudentHoursCSV']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const getInvoicePDF = createAsyncThunk<string, undefined, AsyncThunkConfig>(
  'timesheets/getInvoicePDF',
  async (_, thunkAPI) => {
    try {
      const { timesheetParams, timesheetAdvocates } = thunkAPI.getState().timesheetsSlice;
      const body = {
        ...timesheetParams,
        selected_org_code: timesheetParams.selected_org_code === 'allOrgs' ? null : timesheetParams.selected_org_code,
      };
      const zipAsBase64 = (await axios.post('v0_get_invoice', body)) as string;
      const { month, year, advocate_id } = timesheetParams;
      const blob = b64ToBlob(zipAsBase64, 'application/zip');
      const advocateName = advocate_id
        ? timesheetAdvocates.find((a) => a.id === advocate_id)?.full_name
        : 'All advocates';
      const period = `${months[month - 1].label} ${year}`;
      const currentUser = thunkAPI.getState().authSlice.currentUser;
      const isAdmin = validateAsAdmin(currentUser.role);
      const orgs = thunkAPI.getState().orgsSlice.orgsForAdminDic.all;
      const orgName = orgs.find((o) =>
        isAdmin ? o.code === timesheetParams.selected_org_code : o.code === currentUser.org_code,
      )?.name;
      const zipName = `Invoice${advocate_id ? '' : 's'} ${period} (${advocateName}${orgName ? ' - ' + orgName : ''}).zip`;
      fileSaver.saveAs(blob, zipName);
      return 'file';
    } catch (e) {
      logError(e, ['timesheetsSlice', 'getInvoicePDF']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchTimesheetAdvocates = createAsyncThunk<AdminAdvocate[], undefined, AsyncThunkConfig>(
  'timesheets/fetchTimesheetAdvocates',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_get_leps', {
        filteredInfo: { advocate_type: ['Paid peer'] },
      })) as AdminAdvocate[];
      return response ?? [];
    } catch (e) {
      logError(e, ['timesheetsSlice', 'fetchTimesheetAdvocates']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const timesheetsSlice = createSlice({
  name: 'timesheets',
  initialState,
  reducers: {
    handleTimesheetParamsChange: (state, action: PayloadAction<Partial<TimesheetParams>>) => {
      state.timesheetParams = { ...state.timesheetParams, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getChatLogsCSV.pending, (state, _action) => {
        state.chatLogsCSVApiStatus = APIStatus.PENDING;
      })
      .addCase(getChatLogsCSV.fulfilled, (state, action) => {
        state.chatLogsCSV = action.payload;
        state.chatLogsCSVApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getChatLogsCSV.rejected, (state, _action) => {
        state.chatLogsCSVApiStatus = APIStatus.ERROR;
      })
      .addCase(getPaymentAmountsCSV.pending, (state, _action) => {
        state.timesheetPaymentApiStatus = APIStatus.PENDING;
      })
      .addCase(getPaymentAmountsCSV.fulfilled, (state, action) => {
        state.timesheetPaymentCSV = action.payload;
        state.timesheetPaymentApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getPaymentAmountsCSV.rejected, (state, _action) => {
        state.timesheetPaymentApiStatus = APIStatus.ERROR;
      })
      .addCase(getStudentHoursCSV.pending, (state, _action) => {
        state.timesheetStudentCSVApiStatus = APIStatus.PENDING;
      })
      .addCase(getStudentHoursCSV.fulfilled, (state, action) => {
        state.timesheetStudentCSV = action.payload;
        state.timesheetStudentCSVApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getStudentHoursCSV.rejected, (state, _action) => {
        state.timesheetStudentCSVApiStatus = APIStatus.ERROR;
      })
      .addCase(getInvoicePDF.pending, (state, _action) => {
        state.timesheetInvoiceApiStatus = APIStatus.PENDING;
      })
      .addCase(getInvoicePDF.fulfilled, (state, _action) => {
        state.timesheetInvoiceApiStatus = APIStatus.FULFILLED;
      })
      .addCase(getInvoicePDF.rejected, (state, _action) => {
        state.timesheetInvoiceApiStatus = APIStatus.ERROR;
      })
      .addCase(fetchTimesheetAdvocates.fulfilled, (state, action) => {
        state.timesheetAdvocates = action.payload;
      });
  },
});

export const { handleTimesheetParamsChange } = timesheetsSlice.actions;
